import {useCallback,useEffect,useRef} from 'react';
import {useRememberedState} from '../workspaceMemory';
import {alertRequest} from './alertQueue';
import {useBackgroundRefresh} from '../workspaceUpdates';

const empty=()=>({dashboard:null,alerts:null,incident:null,timeline:null,top:null,collector:null});
export function useDashboardTelemetry({base,token,userGuid,organizationGuid,role}){
 const scope=JSON.stringify([base,token,userGuid,organizationGuid,role]);
 const latest=useRef(scope);latest.current=scope;
 const reads=useRef(null),generation=useRef(0);
 const [state,setState]=useRememberedState(scope,'dashboard',{scope,values:empty(),pending:{},errors:{},forbidden:false},v=>!!v.values.dashboard&&!v.forbidden&&!Object.values(v.errors).some(Boolean)&&!Object.values(v.pending).some(Boolean));
 const refresh=useCallback(async()=>{
  reads.current?.abort();const controller=new AbortController();reads.current=controller;const version=++generation.current;
  const current=()=>!controller.signal.aborted&&version===generation.current&&latest.current===scope;
  if(!token||!userGuid){setState({scope,values:empty(),pending:{},errors:{dashboard:true},forbidden:true});return;}
  const user=new URLSearchParams({userId:userGuid});
  const tasks=[
   ['dashboard',base+'/cybedefender/Dashboard',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({userId:userGuid})}],
   ['alerts',base+'/cybedefender/alerts?'+user+'&limit=20',{}],
   ['incident',base+'/cybedefender/incident-summary?'+user,{}],
   ['timeline',base+'/cybedefender/threat-timeline?'+user+'&limit=10',{}],
   ['top',base+'/cybedefender/top-threats?'+user+'&limit=5',{}],
  ];
  if(organizationGuid)tasks.push(['collector',base+'/cybedefender/syslog-summary?'+new URLSearchParams({organization_guid:organizationGuid}),{}]);
  setState(old=>({scope,values:old.scope===scope?old.values:empty(),pending:Object.fromEntries(tasks.map(([name])=>[name,true])),errors:{},forbidden:false}));
  // Start all independent reads now. Each panel publishes as soon as its own body arrives.
  await Promise.allSettled(tasks.map(async([name,url,options])=>{
   try{
    const value=await alertRequest(url,token,{...options,signal:controller.signal});
    if(!value||typeof value!=='object')throw new Error('invalid_dashboard_response');
    if(name==='alerts'&&!Array.isArray(value)&&!Array.isArray(value.alerts))throw new Error('invalid_dashboard_response');
    if(name!=='alerts'&&Array.isArray(value))throw new Error('invalid_dashboard_response');
    if(name==='timeline'&&value.events!=null&&!Array.isArray(value.events))throw new Error('invalid_dashboard_response');
    if(name==='top'&&value.top_threats!=null&&!Array.isArray(value.top_threats))throw new Error('invalid_dashboard_response');
    if(!current())return;
    setState(old=>old.scope===scope?{...old,values:{...old.values,[name]:value},pending:{...old.pending,[name]:false}}:old);
   }catch(error){
    if(!current())return;
    if([401,403,422].includes(error.status)){
     setState({scope,values:empty(),pending:{},errors:{dashboard:true},forbidden:true});
     controller.abort();
    }else{
     setState(old=>old.scope===scope?{...old,values:error.status===404&&name!=='dashboard'?{...old.values,[name]:null}:old.values,pending:{...old.pending,[name]:false},errors:{...old.errors,[name]:error.status!==404||name==='dashboard'}}:old);
    }
   }
  }));
 },[base,token,userGuid,organizationGuid,scope,setState]);
 useEffect(()=>{refresh();return()=>{reads.current?.abort();generation.current+=1;};},[refresh]);
 const current=state.scope===scope?state:{scope,values:empty(),pending:{dashboard:true},errors:{},forbidden:false};
 const refreshing=Object.values(current.pending).some(Boolean);
 useBackgroundRefresh(refresh,{scope,interval:30000,enabled:!!token&&!!userGuid&&!refreshing&&!current.forbidden});
 return {...current.values,pending:current.pending,errors:current.errors,forbidden:current.forbidden,refreshing,refresh};
}
